<template>
  <div>
    <v-row
      v-if="canSaveQuizContent || canSubmitQuizContent"
      justify="end"
      class="mb-3"
    >
      <v-col cols="auto">
        <template v-if="canSaveQuizContent">
          <template v-if="isQuizContentEditing">
            <v-btn
              v-if="quizContent._id"
              depressed
              color="error"
              :min-width="minWidth"
              class="rounded-1 ms-4"
              :disabled="isQuizContentSaving"
              @click="onCancel"
            >
              {{
                isSaveButtonDisabled
                  ? $t("course.courseDetails.close")
                  : $t("course.courseDetails.cancel")
              }}
            </v-btn>

            <v-btn
              depressed
              color="success"
              :min-width="minWidth"
              class="rounded-1 ms-4"
              :loading="isQuizContentSaving"
              :disabled="isSaveButtonDisabled"
              @click="saveQuizContent"
            >
              {{ $t("course.courseDetails.save") }}
            </v-btn>
          </template>

          <v-btn
            v-else
            depressed
            color="primary"
            :min-width="minWidth"
            class="rounded-1 ms-4"
            @click="isQuizContentEditing = true"
          >
            {{ $t("course.courseDetails.edit") }}
          </v-btn>
        </template>

        <v-btn
          v-if="canSubmitQuizContent"
          depressed
          color="primary"
          :min-width="minWidth"
          class="rounded-1 ms-4"
          :loading="isQuizContentSubmitting"
          @click="submitQuizContentForApproval"
        >
          {{ $t("course.courseDetails.submit") }}
        </v-btn>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { createOrUpdateQuizContent } from "@/app/quiz-module/methods/createOrUpdateOrDeleteQuizContent";
import { submitQuizContentForApproval } from "@/app/quiz-module/methods/submitQuizContentForApproval";
import { hasPermission } from "@/app/user-module/helpers/authHelper";

export default {
  name: "QuizSaveAndSubmit",
  props: {
    quizContent: {
      type: Object,
      required: true,
    },
    isSaving: {
      type: Boolean,
      required: true,
    },
    isEditing: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      isQuizContentSubmitting: false,
    };
  },
  computed: {
    minWidth() {
      return this.$vuetify.breakpoint.smAndDown ? "90" : "120";
    },
    isSaveButtonDisabled() {
      const hasInvalidData =
        this.quizContent.hasInvalidTitle ||
        !this.quizContent.title?.find(x => x.value?.trim()?.length) ||
        this.quizContent.quiz?.questionAnswers?.some(
          q => q.hasInvalidQuestion || q.hasInvalidChoices
        );

      const hasNoChanges = !(
        this.quizContent.hasChangesInTitle ||
        this.quizContent.hasChangesInDescription ||
        this.quizContent.hasChangesInImages ||
        this.quizContent.hasChangesInQuiz ||
        this.quizContent.hasChangesInIsPublic
      );

      return hasInvalidData || hasNoChanges;
    },
    canSaveQuizContent() {
      return (
        this.quizContent._id == null ||
        this.$store.state.authenticatedUserRole === "ADMIN" ||
        (this.$store.state.authenticatedUserRole === "EMPLOYEE" &&
          this.havePermissionUpdateQuizContent)
      );
    },
    canSubmitQuizContent() {
      return (
        this.quizContent._id != null &&
        this.$store.state.authenticatedUser?._id ===
          this.quizContent.createdBy?._id &&
        (this.quizContent.approvalStatus == null ||
          this.quizContent.approvalStatus === "PREPARING") &&
        !this.isQuizContentEditing
      );
    },
    isQuizContentSaving: {
      get() {
        return this.isSaving;
      },
      set(value) {
        this.$emit("update:isSaving", value);
      },
    },
    isQuizContentEditing: {
      get() {
        return this.isEditing;
      },
      set(value) {
        this.$emit("update:isEditing", value);
      },
    },
    havePermissionUpdateQuizContent() {
      return hasPermission({
        currentRole: this.$store.state.authenticatedUserRole,
        currentPermissions: this.$store.state.authenticatedUserPermissions,
        requiredPermission: ["UPDATE_QUIZ"],
        requiredAll: true,
        restrictOrganizationManager:
          this.$store.state.authenticatedUser?.isOrganizationManager,
      });
    },
  },
  methods: {
    saveQuizContent() {
      if (this.isQuizContentSaving) return;
      this.isQuizContentSaving = true;

      createOrUpdateQuizContent(this.quizContent).then(response => {
        if (response.data) {
          this.$emit("onSuccess");

          if (!this.quizContent?._id) {
            this.$router.replace({
              name: "QuizDetails",
              params: { quizId: response.data._id },
            });
          }
        } else if (response.error) this.$emit("onError");

        this.isQuizContentSaving = false;
      });
    },
    submitQuizContentForApproval() {
      if (this.isQuizContentSubmitting) return;
      this.isQuizContentSubmitting = true;

      submitQuizContentForApproval({
        id: this.quizContent?._id,
      }).then(response => {
        if (response.data) this.$emit("onSuccess");
        else if (response.error) this.$emit("onError");

        this.isQuizContentSubmitting = false;
      });
    },
    onCancel() {
      this.isQuizContentEditing = false;
      this.$emit("onCancel");
    },
  },
};
</script>

<style lang="scss" scoped />
